// Whole-brief pulse panel. Three required answers gate the Continue button:
// initialScore (1..10), nowScore (1..10), effort (light|standard|heavy|slog).
const EFFORT_OPTIONS = [
  { value: 'light', label: 'Light' },
  { value: 'standard', label: 'Standard' },
  { value: 'heavy', label: 'Heavy' },
  { value: 'slog', label: 'Slog' },
];

const SCORE_VALUES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

function ScoreRow({ label, value, onChange }) {
  return (
    <div>
      <div className="font-display text-[10px] font-bold tracking-display-md uppercase text-hxd-muted mb-2">
        {label}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {SCORE_VALUES.map((n) => {
          const active = value === n;
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange(active ? null : n)}
              className={
                active
                  ? 'w-8 h-8 rounded-[3px] bg-hxd-orange text-white font-display text-[12px] font-bold'
                  : 'w-8 h-8 rounded-[3px] border border-hxd-border bg-white text-hxd-ink font-display text-[12px] font-bold hover:border-hxd-orange'
              }
            >
              {n}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function BriefPulse({ pulse, onChange, onContinue, continueDisabled }) {
  const initialScore = pulse?.initialScore || null;
  const nowScore = pulse?.nowScore || null;
  const effort = pulse?.effort || null;
  const comment = pulse?.comment || '';

  const allAnswered = !!(initialScore && nowScore && effort);
  const continueReady = allAnswered && !continueDisabled;

  const missing = [];
  if (!initialScore) missing.push('pipeline score');
  if (!nowScore) missing.push('post-edit score');
  if (!effort) missing.push('effort');

  return (
    <div className="hxd-card mb-5">
      <div className="hxd-card-header">Brief Pulse</div>
      <p className="text-[12px] text-hxd-muted mb-4">
        Quick read on the brief itself. All three answers required before Continue.
      </p>

      <div className="space-y-4">
        <ScoreRow
          label="As delivered by the pipeline (1 = unusable, 10 = ship as is)"
          value={initialScore}
          onChange={(v) => onChange({ initialScore: v })}
        />

        <ScoreRow
          label="After your edits (1 = still rough, 10 = great)"
          value={nowScore}
          onChange={(v) => onChange({ nowScore: v })}
        />

        <div>
          <div className="font-display text-[10px] font-bold tracking-display-md uppercase text-hxd-muted mb-2">
            How much work did it take?
          </div>
          <div className="flex flex-wrap gap-2">
            {EFFORT_OPTIONS.map((opt) => {
              const active = effort === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => onChange({ effort: active ? null : opt.value })}
                  className={
                    active
                      ? 'px-3 py-1.5 rounded-[3px] border bg-hxd-orange text-white border-hxd-orange text-[12px] font-bold'
                      : 'px-3 py-1.5 rounded-[3px] border bg-white text-hxd-ink border-hxd-border text-[12px] font-bold hover:border-hxd-orange'
                  }
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="hxd-label">Comment (optional)</label>
          <input
            className="hxd-input"
            value={comment}
            onChange={(e) => onChange({ comment: e.target.value })}
            placeholder="One-line note — what stood out today?"
          />
        </div>
      </div>

      {onContinue && (
        <div className="mt-6 pt-5 border-t border-hxd-border-soft flex flex-wrap items-center justify-between gap-3">
          <span className="text-[12px] text-hxd-muted">
            {allAnswered
              ? 'All set — ready to send to final check.'
              : `Still need: ${missing.join(', ')}.`}
          </span>
          <button
            type="button"
            onClick={onContinue}
            disabled={!continueReady}
            className={
              continueReady
                ? 'group inline-flex items-center gap-2 px-5 py-2.5 rounded-[3px] bg-hxd-orange hover:bg-hxd-orange-bright text-white font-display text-[12px] font-bold uppercase tracking-display-md shadow-sm transition-colors'
                : 'inline-flex items-center gap-2 px-5 py-2.5 rounded-[3px] bg-hxd-border text-hxd-muted font-display text-[12px] font-bold uppercase tracking-display-md cursor-not-allowed'
            }
          >
            Continue to final check
            <span aria-hidden="true" className="transition-transform group-hover:translate-x-0.5">
              →
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
